import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

export type AuthUser = {
    user_id: number;
    username: string;
    full_name: string;
    role: string;
    role_id?: number | null;
    is_active: boolean;
    permissions?: string[];
    visibilities?: string[];
    module_access?: string[];
};

type AuthContextValue = {
    user: AuthUser | null;
    isLoading: boolean;
    hasPermission: (permissionKey: string) => boolean;
    hasVisibility: (visibilityKey: string) => boolean;
    hasModuleAccess: (moduleCode: string) => boolean;
    login: (username: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    const refresh = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include' });
            const data = await res.json();
            setUser(data?.user || null);
        } catch (error) {
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    useEffect(() => {
        const handler = () => {
            setUser(null);
            if (window.location.pathname !== '/login') {
                navigate('/login', { replace: true });
            }
        };
        window.addEventListener('auth:unauthorized', handler);
        return () => window.removeEventListener('auth:unauthorized', handler);
    }, [navigate]);

    const login = async (username: string, password: string) => {
        setIsLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
                credentials: 'include'
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data?.detail || 'Login failed');
            }
            setUser(data?.user || null);
        } finally {
            setIsLoading(false);
        }
    };

    const logout = async () => {
        try {
            await fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST', credentials: 'include' });
        } finally {
            setUser(null);
        }
    };

    const hasPermission = (permissionKey: string): boolean => {
        if (!user || !permissionKey) return false;
        if ((user.role || '').toLowerCase() === 'admin') return true;
        return Array.isArray(user.permissions) && user.permissions.includes(permissionKey);
    };

    const hasModuleAccess = (moduleCode: string): boolean => {
        if (!user || !moduleCode) return false;
        if ((user.role || '').toLowerCase() === 'admin') return true;
        return Array.isArray(user.module_access) && user.module_access.includes(moduleCode);
    };

    const hasVisibility = (visibilityKey: string): boolean => {
        if (!user || !visibilityKey) return false;
        if ((user.role || '').toLowerCase() === 'admin') return true;
        if (!Array.isArray(user.visibilities)) return true;
        return user.visibilities.includes(visibilityKey);
    };

    const value = useMemo(() => ({
        user,
        isLoading,
        hasPermission,
        hasVisibility,
        hasModuleAccess,
        login,
        logout,
        refresh
    }), [user, isLoading]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
    const { user, isLoading } = useAuth();
    const location = useLocation();
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-slate-500">
                Checking session...
            </div>
        );
    }
    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return <>{children}</>;
}

export function RequirePermission({ permission, children }: { permission: string; children: React.ReactNode }) {
    const { user, isLoading, hasPermission } = useAuth();
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-slate-500">
                Checking permissions...
            </div>
        );
    }
    if (!user || !hasPermission(permission)) {
        return <Navigate to="/" replace />;
    }
    return <>{children}</>;
}
